import { AlertCircle, IdCard, Loader2, UserCircle2 } from 'lucide-react';
import * as React from 'react';

import { useMobile } from '../../hooks/use-mobile';
import { cn } from '../../lib/utils';
import type { IPhotoStorageAdapter, PhotoUploadLabels, UploadedPhoto } from '../photo-upload';
import { PhotoUpload } from '../photo-upload/photo-upload';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Textarea } from '../ui/textarea';

/**
 * OneTimeGrantDialog — STORY-1210 ONE_TIME AuthorizationGrant create modal
 * (ADR-124 D-2 + Am.1 #1, Sally 03-partner §3.5).
 *
 * Composition over monolithic:
 *  - Parent controls `open` + `onSubmit(payload)` → service call ownership.
 *  - PhotoUpload (B-04) reused for ID document capture — mobile camera +
 *    drag-drop + clipboard paste. Adapter injected by parent (Gate-7 D1).
 *  - SKIP-with-reason fallback (Am.1 #1): explicit checkbox + textarea, only
 *    enabled when `documentIds.length === 0`. When checked, submit emits
 *    `{ skipDocumentUpload: true, skipReason, documentIds: [] }` — service
 *    validates + starts 24h grace-period clock.
 *
 * Submit payload shape matches `CreateOneTimeGrantDto` shape fields:
 *   { granteeData, documentIds, skipDocumentUpload, skipReason?, gracePeriodHours? }
 * — but NOT the full DTO; parent adds rentalId, grantorPartnerId, createdBy.
 *
 * Mobile responsive: Dialog (desktop) + Sheet (mobile) via useMobile().
 * forceVariant escape hatch for tests (same pattern as shared-package dialog).
 */

export type OneTimeGrantDialogVariant = 'desktop' | 'mobile';

export interface OneTimeGrantGranteeDataInput {
  name: string;
  idCardNumber: string;
  address: string;
  birthDate: string;
  birthPlace: string;
  motherName: string;
  phone: string;
}

export interface OneTimeGrantSubmitPayload {
  granteeData: OneTimeGrantGranteeDataInput;
  documentIds: string[];
  skipDocumentUpload: boolean;
  /** Present iff `skipDocumentUpload === true`. */
  skipReason?: string;
}

export interface OneTimeGrantDialogLabels {
  title: string;
  subtitle: string;
  nameLabel: string;
  namePlaceholder: string;
  idCardNumberLabel: string;
  idCardNumberPlaceholder: string;
  addressLabel: string;
  addressPlaceholder: string;
  birthDateLabel: string;
  birthPlaceLabel: string;
  birthPlacePlaceholder: string;
  motherNameLabel: string;
  motherNamePlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  documentsLabel: string;
  documentsHint: string;
  skipCheckboxLabel: string;
  skipReasonLabel: string;
  skipReasonPlaceholder: string;
  skipWarning: string;
  submitLabel: string;
  submittingLabel: string;
  cancelLabel: string;
  validationNameRequired: string;
  validationDocumentsRequired: string;
  validationSkipReasonRequired: string;
  photoUploadLabels?: PhotoUploadLabels;
}

const DEFAULT_LABELS: OneTimeGrantDialogLabels = {
  title: 'Egyszeri meghatalmazás',
  subtitle: 'Add meg az átvevő adatait és töltsd fel a személyi okmányt.',
  nameLabel: 'Teljes név',
  namePlaceholder: 'pl. Kovács János',
  idCardNumberLabel: 'Személyi igazolvány szám',
  idCardNumberPlaceholder: 'pl. 123456AB',
  addressLabel: 'Lakcím',
  addressPlaceholder: 'pl. 1011 Budapest, Fő utca 1.',
  birthDateLabel: 'Születési dátum',
  birthPlaceLabel: 'Születési hely',
  birthPlacePlaceholder: 'pl. Budapest',
  motherNameLabel: 'Anyja neve',
  motherNamePlaceholder: 'pl. Szabó Anna',
  phoneLabel: 'Telefonszám',
  phonePlaceholder: '+36 ...',
  documentsLabel: 'Személyi okmány feltöltés (kötelező)',
  documentsHint: 'Előlap + hátlap, vagy jogosítvány első oldal. Max 5 fájl.',
  skipCheckboxLabel: 'Okmány feltöltés kihagyása (24 órás türelmi időszak)',
  skipReasonLabel: 'Indoklás (miért hagyjuk ki most?)',
  skipReasonPlaceholder: 'pl. kamera hibás, rossz fényviszonyok, offline helyszín',
  skipWarning:
    '24 órán belül fel kell tölteni az okmányt, különben a rendszer automatikusan visszavonja a meghatalmazást és értesíti mindkét felet.',
  submitLabel: 'Meghatalmazás létrehozása',
  submittingLabel: 'Mentés...',
  cancelLabel: 'Mégse',
  validationNameRequired: 'Teljes név kötelező',
  validationDocumentsRequired: 'Legalább egy okmány fotó kötelező (vagy jelöld be a kihagyást)',
  validationSkipReasonRequired: 'Kihagyás esetén indoklás kötelező',
};

export interface OneTimeGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the validated payload. Parent adds rentalId/grantorPartnerId/createdBy. */
  onSubmit: (payload: OneTimeGrantSubmitPayload) => Promise<void> | void;
  /** Injected photo storage adapter (MinIO/S3 signed PUT). */
  photoAdapter: IPhotoStorageAdapter;
  labels?: Partial<OneTimeGrantDialogLabels>;
  forceVariant?: OneTimeGrantDialogVariant;
  /** Initial values for edit-like flows (rare — ONE_TIME is typically created fresh). */
  initialGranteeData?: Partial<OneTimeGrantGranteeDataInput>;
}

const EMPTY_GRANTEE_DATA: OneTimeGrantGranteeDataInput = {
  name: '',
  idCardNumber: '',
  address: '',
  birthDate: '',
  birthPlace: '',
  motherName: '',
  phone: '',
};

export function OneTimeGrantDialog({
  open,
  onOpenChange,
  onSubmit,
  photoAdapter,
  labels: labelOverrides,
  forceVariant,
  initialGranteeData,
}: OneTimeGrantDialogProps) {
  const mobileAuto = useMobile();
  const variant: OneTimeGrantDialogVariant = forceVariant ?? (mobileAuto ? 'mobile' : 'desktop');
  const labels = React.useMemo(() => ({ ...DEFAULT_LABELS, ...labelOverrides }), [labelOverrides]);

  const [granteeData, setGranteeData] = React.useState<OneTimeGrantGranteeDataInput>(() => ({
    ...EMPTY_GRANTEE_DATA,
    ...initialGranteeData,
  }));
  const [documents, setDocuments] = React.useState<UploadedPhoto[]>([]);
  const [skipUpload, setSkipUpload] = React.useState(false);
  const [skipReason, setSkipReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const inFlightRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // B-12 Blind Hunter P2 #3 / Edge P2 #1 — snapshot initialGranteeData
  // in a ref so effect-based reset uses a stable reference. Previously
  // `[open, initialGranteeData]` would wipe user input mid-edit if the
  // parent passed an inline literal (new identity every render).
  const initialGranteeDataRef = React.useRef(initialGranteeData);
  // Reset on open=true (fresh dialog session). Avoid resetting on close →
  // user's in-progress data survives if parent toggles open rapidly mid-edit.
  const prevOpenRef = React.useRef(open);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Snapshot the current prop at open-transition; safe against identity
      // churn for the rest of this dialog session.
      initialGranteeDataRef.current = initialGranteeData;
      setGranteeData({ ...EMPTY_GRANTEE_DATA, ...initialGranteeData });
      setDocuments([]);
      setSkipUpload(false);
      setSkipReason('');
      setSubmitError(null);
    }
    prevOpenRef.current = open;
    // intentional — depending only on `open` prevents mid-edit resets when
    // the parent re-renders with a new `initialGranteeData` identity.
  }, [open]);

  // B-12 Edge Case Hunter P2 #2 — auto-uncheck skip only on 0→>0 transition.
  // Previously re-fired on every `documents.length` change (deletes included),
  // which was functionally harmless but prevented re-enabling skip after
  // accidental upload+delete without manual click.
  const prevDocsCountRef = React.useRef(documents.length);
  React.useEffect(() => {
    const prev = prevDocsCountRef.current;
    const curr = documents.length;
    if (prev === 0 && curr > 0 && skipUpload) {
      setSkipUpload(false);
      setSkipReason('');
    }
    prevDocsCountRef.current = curr;
  }, [documents.length, skipUpload]);

  const updateField = React.useCallback(
    <K extends keyof OneTimeGrantGranteeDataInput>(
      key: K,
      value: OneTimeGrantGranteeDataInput[K]
    ) => {
      setGranteeData(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  // B-12 Blind Hunter P2 #4 — effective skip derived from state, NOT
  // mutated. If the user clicked skip, then uploaded (triggering auto-
  // uncheck), then clicked Submit before React re-rendered, the old
  // validation would flash the wrong message. Deriving `effectiveSkip` at
  // validate-time forces a single source of truth.
  const effectiveSkip = skipUpload && documents.length === 0;

  const validate = React.useCallback((): string | null => {
    if (granteeData.name.trim().length === 0) return labels.validationNameRequired;
    if (documents.length === 0 && !effectiveSkip) return labels.validationDocumentsRequired;
    if (effectiveSkip && skipReason.trim().length === 0) return labels.validationSkipReasonRequired;
    return null;
  }, [
    granteeData.name,
    documents.length,
    effectiveSkip,
    skipReason,
    labels.validationNameRequired,
    labels.validationDocumentsRequired,
    labels.validationSkipReasonRequired,
  ]);

  const handleSubmit = React.useCallback(async () => {
    if (inFlightRef.current) return;
    const err = validate();
    if (err) {
      setSubmitError(err);
      return;
    }
    inFlightRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload: OneTimeGrantSubmitPayload = {
        granteeData,
        documentIds: documents.map(d => d.id),
        skipDocumentUpload: effectiveSkip,
        ...(effectiveSkip ? { skipReason: skipReason.trim() } : {}),
      };
      await onSubmit(payload);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex);
      if (isMountedRef.current) {
        setSubmitError(msg);
      }
    } finally {
      inFlightRef.current = false;
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [validate, granteeData, documents, effectiveSkip, skipReason, onSubmit]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      // Mid-submit: don't allow close (tearing protection).
      if (inFlightRef.current) return;
      onOpenChange(next);
    },
    [onOpenChange]
  );

  const body = (
    <div className="space-y-3 py-2">
      {submitError && (
        <Alert variant="destructive" data-testid="one-time-grant-error">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <fieldset className="space-y-2" disabled={submitting}>
        <legend className="sr-only">Átvevő adatai</legend>

        <div>
          <Label htmlFor="one-time-grant-name">{labels.nameLabel} *</Label>
          <div className="relative mt-1">
            <UserCircle2 className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="one-time-grant-name"
              className="pl-8"
              required
              value={granteeData.name}
              placeholder={labels.namePlaceholder}
              onChange={e => updateField('name', e.target.value)}
              data-testid="one-time-grant-name"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="one-time-grant-idcard">{labels.idCardNumberLabel}</Label>
            <div className="relative mt-1">
              <IdCard className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="one-time-grant-idcard"
                className="pl-8"
                value={granteeData.idCardNumber}
                placeholder={labels.idCardNumberPlaceholder}
                onChange={e => updateField('idCardNumber', e.target.value)}
                data-testid="one-time-grant-idcard"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="one-time-grant-phone">{labels.phoneLabel}</Label>
            <Input
              id="one-time-grant-phone"
              className="mt-1"
              value={granteeData.phone}
              placeholder={labels.phonePlaceholder}
              onChange={e => updateField('phone', e.target.value)}
              data-testid="one-time-grant-phone"
              type="tel"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="one-time-grant-address">{labels.addressLabel}</Label>
          <Input
            id="one-time-grant-address"
            className="mt-1"
            value={granteeData.address}
            placeholder={labels.addressPlaceholder}
            onChange={e => updateField('address', e.target.value)}
            data-testid="one-time-grant-address"
          />
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="one-time-grant-birthdate">{labels.birthDateLabel}</Label>
            <Input
              id="one-time-grant-birthdate"
              className="mt-1"
              type="date"
              value={granteeData.birthDate}
              onChange={e => updateField('birthDate', e.target.value)}
              data-testid="one-time-grant-birthdate"
            />
          </div>
          <div>
            <Label htmlFor="one-time-grant-birthplace">{labels.birthPlaceLabel}</Label>
            <Input
              id="one-time-grant-birthplace"
              className="mt-1"
              value={granteeData.birthPlace}
              placeholder={labels.birthPlacePlaceholder}
              onChange={e => updateField('birthPlace', e.target.value)}
              data-testid="one-time-grant-birthplace"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="one-time-grant-mothername">{labels.motherNameLabel}</Label>
          <Input
            id="one-time-grant-mothername"
            className="mt-1"
            value={granteeData.motherName}
            placeholder={labels.motherNamePlaceholder}
            onChange={e => updateField('motherName', e.target.value)}
            data-testid="one-time-grant-mothername"
          />
        </div>
      </fieldset>

      <div className="space-y-2 rounded-md border p-3">
        <Label className="font-medium">{labels.documentsLabel}</Label>
        <p className="text-xs text-muted-foreground">{labels.documentsHint}</p>
        <PhotoUpload
          value={documents}
          onChange={setDocuments}
          adapter={photoAdapter}
          disabled={submitting || skipUpload}
          {...(labels.photoUploadLabels ? { labels: labels.photoUploadLabels } : {})}
          data-testid="one-time-grant-photos"
        />
      </div>

      <div
        className={cn(
          'rounded-md border p-3 transition-colors',
          documents.length === 0 ? 'bg-amber-50/50 dark:bg-amber-950/20' : 'opacity-60'
        )}
      >
        <label className="flex cursor-pointer items-start gap-2">
          <Checkbox
            checked={skipUpload}
            disabled={submitting || documents.length > 0}
            onCheckedChange={checked => setSkipUpload(checked === true)}
            data-testid="one-time-grant-skip"
          />
          <span className="text-sm leading-snug">{labels.skipCheckboxLabel}</span>
        </label>
        {skipUpload && (
          <div className="mt-2 space-y-2">
            <Alert
              variant="default"
              className="border-amber-300 bg-amber-100/60 dark:bg-amber-900/30"
            >
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription className="text-xs">{labels.skipWarning}</AlertDescription>
            </Alert>
            <div>
              <Label htmlFor="one-time-grant-skip-reason">{labels.skipReasonLabel} *</Label>
              <Textarea
                id="one-time-grant-skip-reason"
                className="mt-1"
                rows={2}
                value={skipReason}
                placeholder={labels.skipReasonPlaceholder}
                onChange={e => setSkipReason(e.target.value)}
                disabled={submitting}
                maxLength={500}
                data-testid="one-time-grant-skip-reason"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button
        type="button"
        variant="ghost"
        disabled={submitting}
        onClick={() => handleOpenChange(false)}
        data-testid="one-time-grant-cancel"
      >
        {labels.cancelLabel}
      </Button>
      <Button
        type="button"
        disabled={submitting}
        onClick={() => void handleSubmit()}
        data-testid="one-time-grant-submit"
      >
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {submitting ? labels.submittingLabel : labels.submitLabel}
      </Button>
    </div>
  );

  if (variant === 'mobile') {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="max-h-[92vh] overflow-y-auto"
          data-testid="one-time-grant-dialog-mobile"
        >
          <SheetHeader>
            <SheetTitle>{labels.title}</SheetTitle>
            <SheetDescription>{labels.subtitle}</SheetDescription>
          </SheetHeader>
          {body}
          <SheetFooter>{footer}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="one-time-grant-dialog-desktop"
      >
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.subtitle}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
